import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import {
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Switch,
  Text,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { CiLock } from 'react-icons/ci';
import { useSelector } from 'react-redux';

import { AlertDialogModal } from 'clipsal-cortex-ui/src/components/AlertDialogModal';

import { getShutdownAlertUserPreference } from '../../account/settings/settings-helpers';
import { selectSite } from '../../site/siteSlice';
import { useGetLiveSwitchQuery, useUpdateSwitchMutation } from './switchApi';

type Props = {
  switchId: number;
};

export function SwitchToggle({ switchId }: Props) {
  const { data: device, isLoading } = useGetLiveSwitchQuery(switchId);
  const [pendingSwitchUpdate, setPendingSwitchUpdate] = useState<'open' | 'closed' | null>();
  const [updateSwitch, { isLoading: updateIsLoading }] = useUpdateSwitchMutation();
  const toast = useToast({ isClosable: true });
  const { isOpen: isShutdownAlertOpen, onOpen: onOpenShutdownAlert, onClose: onCloseShutdownAlert } = useDisclosure();
  const [updateUserPreferenceRequested, setUpdateUserPreferenceRequested] = useState<boolean>(false);
  const { site_id: siteId } = useSelector(selectSite);
  const { t } = useTranslation();
  const showShutdownAlert = getShutdownAlertUserPreference();

  const isControlModeOff = device?.control_mode === 'off';
  const switchIsDisabled =
    isLoading || updateIsLoading || !!pendingSwitchUpdate || device?.status === 'offline' || isControlModeOff;
  const deviceIsOn =
    device && device.status === 'online' && (device.state === 'closed' || (!updateIsLoading && device.power > 0));

  /* Hacky solution for an optimistic update on a switch update.
   * 1. We PATCH via the HTTP API - it's not immediately reflected in the live data APIs
   * 2. Flag the switch via state (pendingSwitchUpdate), ignore live data until it matches the new state, or it errors
   */
  useEffect(() => {
    if (device?.state === pendingSwitchUpdate) {
      setPendingSwitchUpdate(null);
    }
  }, [device?.state, device?.power, pendingSwitchUpdate]);

  const handleSwitchToggle = useCallback(
    async (toggleOnRequested: boolean) => {
      const requestedStateForApi = toggleOnRequested ? 'closed' : 'open';
      try {
        if (!device) throw Error('Device not found');
        setPendingSwitchUpdate(requestedStateForApi); // 'Hacky' optimistic update  - see above
        await updateSwitch({ siteId, switchId: device.id, body: { state: requestedStateForApi } }).unwrap();
        setPendingSwitchUpdate(requestedStateForApi);
      } catch (error) {
        console.error(error);
        toast({
          status: 'error',
          title: t('Devices.error updating device'),
          description: `${t('Common.please try again')} ${t('Common.if this persists contact support')}`,
        });
        setPendingSwitchUpdate(null); // Unset the pending state on error
      }
    },
    [device, siteId, t, toast, updateSwitch]
  );

  return (
    <>
      <Popover>
        <PopoverTrigger>
          <IconButton
            hidden={!isControlModeOff}
            size={'lg'}
            aria-label={'Control mode off'}
            variant={'ghost'}
            icon={<CiLock size="1.5em" />}
            data-testid={`switch-${switchId}-control-mode-popover-button`}
            marginLeft="auto"
          />
        </PopoverTrigger>
        <PopoverContent borderRadius={8} w="200px" border="none" data-testid="control-mode-overridden-popover-content">
          <PopoverArrow />
          <PopoverCloseButton data-testid="close-control-mode-overridden-popover-button" />
          <PopoverBody borderRadius="14px" boxShadow="rgba(0, 0, 0, 0.3) 0px 0px 10px 0px" paddingTop="1em">
            <Text>{t('Devices.control mode off')}</Text>
          </PopoverBody>
        </PopoverContent>
      </Popover>

      <Switch
        isChecked={pendingSwitchUpdate ? pendingSwitchUpdate === 'closed' : deviceIsOn}
        onChange={(e: ChangeEvent<HTMLInputElement>) => {
          if (!e.target.checked && showShutdownAlert) {
            onOpenShutdownAlert();
          } else handleSwitchToggle(e.target.checked);
        }}
        variant="solarGreen"
        size="lg"
        data-testid={`switch-${switchId}-toggle`}
        isDisabled={switchIsDisabled}
      />
      <AlertDialogModal
        isOpen={isShutdownAlertOpen}
        onClose={onCloseShutdownAlert}
        header={t('Devices.turn off device?', { name: device?.site_switch_label })}
        subHeader={t('Devices.are you sure turn off?')}
        confirmButtonName={t('Devices.turn off')}
        cancelButtonName={t('Common.cancel')}
        onConfirm={() => {
          updateUserPreferenceRequested && localStorage.setItem('showShutdownAlerts', 'false');
          handleSwitchToggle(false);
        }}
        onSelectDontShowAgain={() => setUpdateUserPreferenceRequested(true)}
        dontShowRadioText={t('Common.dont show alerts anymore')}
      />
    </>
  );
}
